/**
 * React NodeView for the `[[wikilink]]` node.
 *
 * Resolves the chip's broken-state at render time by looking up
 * `node.attrs.nodeId` in `useWikiTree().nodesById`. When the target is
 * missing from the cache OR has been soft-deleted, `data-broken="true"`
 * flips on the rendered element and the broken-link CSS treatment
 * (rose-tinted bg + strikethrough, see `src/styles/global.css`) fires.
 *
 * The chip IS the anchor — `<NodeViewWrapper as="a">` so the browser
 * handles `target="_blank"` natively (plain / Cmd / middle / right-click
 * → "Open in new tab" all work). This is the same model the static
 * `renderHTML` uses for non-editor rendering paths (e.g. HTML
 * copy/paste); the NodeView just adds the React-driven `data-broken`
 * resolution that `renderHTML` can't do without a hook context.
 *
 * Markdown round-trip is independent of the broken-state — the chip
 * still serializes to `[[id|name]]` regardless of whether the target
 * exists, so a P3 "restore deleted page" feature auto-resolves any
 * existing wikilinks pointing at it without a data migration.
 */

import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import { useTranslation } from 'react-i18next'

import { useWikiTree } from '@/features/wiki/hooks/use-wiki-tree'

export function WikiLinkNodeView({ node }: NodeViewProps) {
  const { t } = useTranslation('wiki')
  const id = String(node.attrs.nodeId ?? '')
  const label = String(node.attrs.nodeName ?? id)

  // `nodesById` is the canonical "every node the user can see" map.
  // A miss here means either (a) the target was soft-deleted (the BE
  // tree query excludes `deletedAt IS NOT NULL` rows) or (b) the
  // caller's role doesn't grant access. Both render the same way
  // visually — the user can't open it either way.
  const { nodesById } = useWikiTree()
  const target = nodesById.get(id)
  const isBroken = !target || target.deletedAt !== null

  return (
    <NodeViewWrapper
      as="a"
      href={`/wiki/d/${id}`}
      target="_blank"
      rel="noopener noreferrer"
      className="wikilink-chip"
      data-type="wikilink"
      data-id={id}
      data-label={label}
      data-broken={isBroken ? 'true' : undefined}
      title={isBroken ? t('wikilink.brokenTooltip') : undefined}
    >
      {label}
    </NodeViewWrapper>
  )
}
